import React, { useContext, useMemo, useState } from "react";
import { Trash2, Users } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderRecipientType from "./api";
import { recipientTypeFields } from "./features/recipient-type.fields";
import { recipientTypeValidation } from "./features/recipient-type.validation";
import { loadRecipientTypeDependencies } from "./features/recipient-type.dependencies";
import { recipientTypeFilterFields } from "./features/recipient-type.filter";
import { toRecipientTypePayload } from "./features/recipient-type.utils";
import { getRecipientTypeColumns } from "./recipient-type-list/recipientTypeColumns";
import RecipientTypeRowActions from "./recipient-type-list/RecipientTypeRowActions";
import RecipientTypeViewDrawer from "./recipient-type-dialogs/RecipientTypeViewDrawer";

const createRecipientType = (values) => apiReminderRecipientType.create(toRecipientTypePayload(values, false));
const updateRecipientType = (id, values) => apiReminderRecipientType.update(id, toRecipientTypePayload(values, true));

// Reminder Setup -> Recipient Type (tbl_rem_recipient_type): who a reminder rule step can send to.
const RecipientType = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderRecipientType.getAll, { loadError: "Failed to load recipient types." });
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [form, setForm] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const deleteRecipientTypes = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Recipient Type",
      message: `Delete ${ids.length > 1 ? `${ids.length} recipient types` : "this recipient type"}? Types used by a reminder rule step can't be deleted.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderRecipientType.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const columns = useMemo(() => getRecipientTypeColumns(list.start), [list.start]);

  const renderRowActions = (row) => (
    <RecipientTypeRowActions
      row={row}
      canManage={canManage}
      onView={setViewRecord}
      onEdit={(target) => setForm({ id: target.id })}
      onDelete={(target) => deleteRecipientTypes([target.id])}
    />
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          onCreate={() => setForm({ id: null })}
          onDeleteSelected={canManage ? () => deleteRecipientTypes(list.selectedRows) : undefined}
          selectedCount={list.selectedRows.length}
          resetSearchTrigger={list.resetTrigger}
          canCreate={canManage}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={recipientTypeFilterFields} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={Users}
              title={list.hasQuery ? "No recipient types match" : "No recipient types yet"}
              message={
                list.hasQuery
                  ? "No recipient types match your search or filters. Try adjusting them."
                  : "Add who reminders can go to: the user a record is about, their assigned admin, a role, fixed addresses and more."
              }
              showBack={false}
              showRefresh={list.hasQuery}
              onRefresh={list.handleRefresh}
              showCreate={canManage && !list.hasQuery}
              createText="Add Recipient Type"
              onCreate={() => setForm({ id: null })}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      <EntityFormModal
        isOpen={Boolean(form)}
        onClose={() => setForm(null)}
        id={form?.id}
        title="Recipient Type"
        fetchById={apiReminderRecipientType.getById}
        createFn={createRecipientType}
        updateFn={updateRecipientType}
        dependenciesLoader={loadRecipientTypeDependencies}
        fields={recipientTypeFields}
        validationSchema={recipientTypeValidation}
        onSuccess={list.reload}
      />

      {viewRecord && <RecipientTypeViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default RecipientType;
